// src/pages/calendar/bookings/Bookings.js - Fixed search and table UI
import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { axiosInstance } from "../../../utils/axios";
import { useSpace } from "../../../contexts/SpaceContext";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

import {
  Alert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Paper as MuiPaper,
  Snackbar,
  Typography,
} from "@mui/material";

import BookingsTable from "./BookingsTable";
import BookingFilters from "./BookingFilters";
import BookingDialogs from "./BookingDialogs";
import { getBookingStatus, formatDateTimeDisplay } from "./utils/bookingUtils";
import { sendEmail } from "./utils/emailUtils";
import SearchBar from "../../components/SearchBar"; // Import SearchBar like in EventTypes.js

// Apply dayjs plugins
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

// Styled components
const Divider = styled(MuiDivider)(({ theme }) => ({
  margin: theme.spacing(3),
}));
const Breadcrumbs = styled(MuiBreadcrumbs)(({ theme }) => ({
  margin: theme.spacing(2, 0),
}));
const Paper = styled(MuiPaper)(({ theme }) => ({
  padding: theme.spacing(2),
}));

// GraphQL queries
// const GET_BOOKINGS_BY_SPACE = `
//   query GetBookingsBySpace($spaceId: ID) {
//     getBookingsBySpace(spaceId: $spaceId) {
//       id
//       date
//       timeStart
//       timeEnd
//       name
//       email
//       notes
//       timezone
//       eventTypeId
//       status
//       createdAt
//       updatedAt
//     }
//   }
// `;

const GET_BOOKINGS_BY_SPACE = `
  query GetBookingsBySpace($spaceId: ID) {
    getBookingsBySpace(spaceId: $spaceId) {
      id
      date
      timeStart
      timeEnd
      name
      email
      notes
      timezone
      eventTypeId
      status
      createdAt
      updatedAt
      externalMeetingId
      managementToken
      meetingInfo {
        link
        roomName
        attendeeName
        hostName
        provider
        createdAt
        status
        revokedAt
      }
    }
  }
`;

// const GET_EVENT_TYPE = `
//   query GetEventType($id: ID!) {
//     getEventType(id: $id) {
//       id
//       title
//       url
//       description
//       duration
//       advanced {
//         requiresConfirmation {
//           enabled
//         }
//       }
//     }
//   }
// `;

const GET_EVENT_TYPE = `
  query GetEventType($id: ID!) {
    getEventType(id: $id) {
      id
      title
      url
      description
      duration
      assignment
      availabilityId
      classification {
        eventFormat
        templateType
        customTemplate
      }
      advanced {
        requiresConfirmation {
          enabled
        }
      }
    }
  }
`;

const GET_AVAILABILITY = `
  query GetAvailability($id: ID!) {
    getAvailability(id: $id) {
      id
      name
      timezone
      days
      timeSlots {
        timeStart
        timeEnd
      }
      dayTimeSlots {
        day
        slots {
          timeStart
          timeEnd
        }
      }
      overrides {
        date
        timeSlots {
          timeStart
          timeEnd
        }
        isUnavailable
      }
    }
  }
`;

// GraphQL mutations
const UPDATE_BOOKING = `
  mutation UpdateBooking($id: ID!, $input: BookingInput!) {
    updateBooking(id: $id, input: $input) {
      id
      date
      timeStart
      timeEnd
      name
      email
      notes
      timezone
      status
      eventTypeId
    }
  }
`;

const DELETE_BOOKING = `
  mutation DeleteBooking($id: ID!) {
    deleteBooking(id: $id) {
      id
      name
      email
      date
      timeStart
      timeEnd
    }
  }
`;

const DELETE_BOOKINGS = `
  mutation DeleteBookings($ids: [ID!]!) {
    deleteBookings(ids: $ids) {
      success
      deletedCount
      deletedIds
      errors
    }
  }
`;

const KILL_MEETING_LINK = `
  mutation KillMeetingLink($bookingId: ID!) {
    killMeetingLink(bookingId: $bookingId) {
      id
      meetingInfo {
        link
        roomName
        provider
        status
        revokedAt
      }
    }
  }
`;

function Bookings() {
  // State management
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [bookings, setBookings] = useState([]);
  const [loadingActions, setLoadingActions] = useState({});
  const [loading, setLoading] = useState(true);
  const [eventTypesData, setEventTypesData] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { currentSpace } = useSpace();

  // Get spaceId and status filter from query params
  const queryParams = new URLSearchParams(location.search);
  const currentSpaceId = queryParams.get("spaceId") || currentSpace?.id;
  const urlStatusFilter = queryParams.get("status");

  // Map URL status params to filter values
  const getInitialFilter = () => {
    if (!urlStatusFilter) return "upcoming";
    const statusMap = {
      pending: "pending",
      confirmed: "upcoming",
      completed: "past",
      canceled: "canceled",
      cancelled: "canceled",
    };
    return statusMap[urlStatusFilter.toLowerCase()] || "upcoming";
  };

  // UI state
  const [selected, setSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  // Use URL param status or "upcoming" as default
  const [filterStatus, setFilterStatus] = useState(getInitialFilter());
  const [viewStyle, setViewStyle] = useState("List");

  // Dialog states
  const [cancelDialog, setCancelDialog] = useState(false);
  const [rescheduleDialog, setRescheduleDialog] = useState(false);
  const [requestRescheduleDialog, setRequestRescheduleDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [selectedBookingIds, setSelectedBookingIds] = useState([]); // For bulk delete
  const [sendingEmail, setSendingEmail] = useState(false);
  const [killLinkDialog, setKillLinkDialog] = useState(false);
  const [killingLink, setKillingLink] = useState(false);

  // Reschedule form data
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState(null);
  const [rescheduleTimezone, setRescheduleTimezone] = useState("");
  const [rescheduleAvailability, setRescheduleAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [rescheduleEventType, setRescheduleEventType] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("date");

  // Fetch event type data for a booking
  const fetchEventTypeData = async (eventTypeId) => {
    try {
      if (eventTypesData[eventTypeId]) {
        return eventTypesData[eventTypeId];
      }

      const response = await axiosInstance.post("/graphql", {
        query: GET_EVENT_TYPE,
        variables: { id: eventTypeId },
      });

      if (response.data?.getEventType) {
        const eventType = response.data.getEventType;
        setEventTypesData((prev) => ({
          ...prev,
          [eventTypeId]: eventType,
        }));
        return eventType;
      }

      return null;
    } catch (error) {
      console.error("Error fetching event type:", error);
      return null;
    }
  };

  // Helper function to check if a booking's date/time has passed
  const isBookingInPast = (booking) => {
    if (!booking.date || !booking.timeEnd) return false;

    try {
      // Parse the booking date and end time
      // Format: date = "2025-12-03", timeEnd = "10:30am" or "10:30 am"
      const bookingDate = booking.date;
      const timeEnd = booking.timeEnd.replace(/\s+/g, "").toLowerCase(); // "10:30am"

      // Parse time - handle both "10:30am" and "10:30 am" formats
      const timeMatch = timeEnd.match(/^(\d{1,2}):(\d{2})(am|pm)$/i);
      if (!timeMatch) return false;

      let hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      const period = timeMatch[3].toLowerCase();

      // Convert to 24-hour format
      if (period === "pm" && hours !== 12) {
        hours += 12;
      } else if (period === "am" && hours === 12) {
        hours = 0;
      }

      // Create booking end datetime
      const bookingEndDateTime = dayjs(
        `${bookingDate} ${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}`
      );

      // Compare with current time
      const now = dayjs();
      return bookingEndDateTime.isBefore(now);
    } catch (error) {
      console.error("Error checking if booking is in past:", error);
      return false;
    }
  };

  // Filter bookings based on search query and status filter
  const filteredBookings = bookings.filter((booking) => {
    // First apply text search filter
    const matchesSearch = !searchQuery
      ? true
      : booking.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        booking.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        booking.id
          ?.toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (booking.notes &&
          booking.notes.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (booking.date &&
          booking.date.toLowerCase().includes(searchQuery.toLowerCase()));

    if (!matchesSearch) return false;

    // Check if booking date/time has passed
    const isPast = isBookingInPast(booking);
    const status = booking.status || "unknown";

    // Apply status filter based on the selected filter value
    switch (filterStatus) {
      case "upcoming":
        // Show as upcoming only if NOT in the past and status is upcoming/confirmed
        // Exclude canceled bookings from upcoming
        if (isPast) return false;
        if (status === "canceled" || status === "cancelled") return false;
        return (
          status === "upcoming" ||
          status === "confirmed" ||
          status === "pending"
        );
      case "unconfirmed":
        // Show unconfirmed only if NOT in the past
        if (isPast) return false;
        return status === "pending";
      case "past":
        // Show as past if the booking date/time has passed OR status is "past"
        // But exclude canceled bookings from past (they go in canceled tab)
        if (status === "canceled" || status === "cancelled") return false;
        return isPast || status === "past";
      case "recurring":
        return false; // Not implemented yet
      case "canceled":
        return status === "canceled" || status === "cancelled";
      default:
        return true;
    }
  });

  // Handle alert close
  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Handle search change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when search changes
  };

  // Handle cancel booking
  const handleCancelBooking = () => {
    if (selected.length === 0) return;
    setSelectedBookingId(selected[0]);
    setCancelDialog(true);
  };

  // Handle reschedule booking - accepts optional bookingId for direct calls
  const handleRescheduleBooking = async (bookingId = null) => {
    // Use provided bookingId or fall back to selected
    const targetBookingId =
      bookingId || (selected.length > 0 ? selected[0] : null);
    if (!targetBookingId) return;
    setSelectedBookingId(targetBookingId);

    // Find the selected booking
    const booking = bookings.find((b) => b.id === targetBookingId);
    if (booking) {
      // Set initial values for reschedule form
      setRescheduleDate(dayjs(booking.date));
      // Parse time like "10:30am"
      setRescheduleTime(
        dayjs(`2000-01-01 ${booking.timeStart.replace(/([ap])m/i, " $1m")}`)
      );
      setRescheduleTimezone(booking.timezone || "UTC");

      // Open dialog first with loading state
      setLoadingAvailability(true);
      setRescheduleDialog(true);

      // Fetch availability data for the event type
      try {
        const eventType = await fetchEventTypeData(booking.eventTypeId);
        console.log("Fetched event type for reschedule:", eventType);

        if (eventType) {
          setRescheduleEventType(eventType); // Store event type for dialog

          if (eventType.availabilityId) {
            console.log(
              "Fetching availability with ID:",
              eventType.availabilityId
            );
            const availResponse = await axiosInstance.post("/graphql", {
              query: GET_AVAILABILITY,
              variables: { id: eventType.availabilityId },
            });

            console.log("Availability response:", availResponse.data);

            let availabilityData = null;
            if (availResponse.data?.data?.getAvailability) {
              availabilityData = availResponse.data.data.getAvailability;
            } else if (availResponse.data?.getAvailability) {
              availabilityData = availResponse.data.getAvailability;
            }

            if (availabilityData) {
              console.log("Setting availability data:", availabilityData);
              setRescheduleAvailability(availabilityData);
            } else {
              console.log("No availability data found, using default");
              // Fallback to default availability (weekdays 9-5)
              setRescheduleAvailability({
                days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                timeSlots: [{ timeStart: "09:00 am", timeEnd: "05:00 pm" }],
                overrides: [],
              });
            }
          } else {
            console.log("No availabilityId on event type, using default");
            // No availability set, use default
            setRescheduleAvailability({
              days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              timeSlots: [{ timeStart: "09:00 am", timeEnd: "05:00 pm" }],
              overrides: [],
            });
          }
        } else {
          console.log("No event type found, using defaults");
          // No event type found, use defaults
          setRescheduleEventType({ duration: 30 });
          setRescheduleAvailability({
            days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            timeSlots: [{ timeStart: "09:00 am", timeEnd: "05:00 pm" }],
            overrides: [],
          });
        }
      } catch (error) {
        console.error("Error fetching availability:", error);
        // Fallback to default availability on error
        setRescheduleEventType({ duration: 30 });
        setRescheduleAvailability({
          days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          timeSlots: [{ timeStart: "09:00 am", timeEnd: "05:00 pm" }],
          overrides: [],
        });
      } finally {
        setLoadingAvailability(false);
      }
    } else {
      // No booking found, just open dialog
      setRescheduleDialog(true);
    }
  };

  // Handle request reschedule
  const handleRequestReschedule = () => {
    if (selected.length === 0) return;
    setSelectedBookingId(selected[0]);
    setRequestRescheduleDialog(true);
  };

  // Handle meeting summary click - navigate to separate page
  const handleMeetingSummaryClick = (booking) => {
    navigate(`/calendar/bookings/${booking.id}`);
  };

  // End an active meeting for a booking
  const handleEndMeeting = async (booking) => {
    if (!window.confirm("Are you sure you want to end this meeting? All participants will be disconnected.")) return;

    try {
      const meetingId = booking.meetingRecordId;
      const response = await axiosInstance.post("/api/meetings/terminate", { meetingId });
      if (response.data?.success) {
        setSnackbar({ open: true, message: "Meeting ended successfully", severity: "success" });
        fetchBookings();
      } else {
        setSnackbar({ open: true, message: response.data?.message || "Failed to end meeting", severity: "error" });
      }
    } catch (err) {
      console.error("Error ending meeting:", err);
      setSnackbar({ open: true, message: "Failed to end meeting", severity: "error" });
    }
  };

  // Confirm cancel booking
  const confirmCancelBooking = async () => {
    if (!selectedBookingId) return;

    try {
      setSendingEmail(true);

      // Find the booking
      const booking = bookings.find((b) => b.id === selectedBookingId);
      if (!booking) throw new Error("Booking not found");

      // Fetch event type data
      const eventType = await fetchEventTypeData(booking.eventTypeId);
      if (!eventType) throw new Error("Event type not found");

      // Send cancellation email - if this fails, continue anyway
      try {
        await sendEmail(booking, eventType, "cancellation", {
          bookingPreviewUrl: `${window.location.origin}/preview/preview-event-type?eventTypeId=${eventType.id}`,
        });
        console.log("Cancellation email sent successfully");
      } catch (emailError) {
        console.warn(
          "Email sending had an issue but we'll continue:",
          emailError
        );
        // Continue with the cancellation even if email fails
      }

      // Update booking status to canceled
      const response = await axiosInstance.post("/graphql", {
        query: UPDATE_BOOKING,
        variables: {
          id: selectedBookingId,
          input: {
            eventTypeId: booking.eventTypeId,
            date: booking.date,
            timeStart: booking.timeStart,
            timeEnd: booking.timeEnd,
            name: booking.name,
            email: booking.email,
            notes: booking.notes,
            timezone: booking.timezone,
            status: "canceled", // Set status to canceled
          },
        },
      });

      if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || "Unknown error");
      }

      // Update local state
      const updatedBookings = bookings.map((b) => {
        if (b.id === selectedBookingId) {
          return {
            ...b,
            status: "canceled",
          };
        }
        return b;
      });

      setBookings(updatedBookings);
      setSelected([]);

      setAlert({
        open: true,
        message: "Booking canceled successfully and email sent to attendee",
        severity: "success",
      });
    } catch (error) {
      console.error("Error canceling booking:", error);
      setAlert({
        open: true,
        message: `Error canceling booking: ${error.message}`,
        severity: "error",
      });
    } finally {
      setSendingEmail(false);
      setCancelDialog(false);
    }
  };

  // Confirm reschedule booking
  const confirmRescheduleBooking = async () => {
    if (!selectedBookingId || !rescheduleDate || !rescheduleTime) {
      setAlert({
        open: true,
        message: "Please select a valid date and time",
        severity: "warning",
      });
      return;
    }

    try {
      setSendingEmail(true);

      // Find the booking
      const booking = bookings.find((b) => b.id === selectedBookingId);
      if (!booking) throw new Error("Booking not found");

      // Fetch event type data
      const eventType = await fetchEventTypeData(booking.eventTypeId);
      if (!eventType) throw new Error("Event type not found");

      // Format new date and time
      const formattedDate = rescheduleDate.format("YYYY-MM-DD");
      const formattedTimeStart = rescheduleTime.format("h:mma");
      const formattedTimeEnd = rescheduleTime
        .add(eventType.duration, "minute")
        .format("h:mma");

      // Check if the new time slot is already booked (excluding current booking)
      const newStartTime = rescheduleTime;
      const newEndTime = rescheduleTime.add(eventType.duration, "minute");

      const conflictingBooking = bookings.find((b) => {
        // Skip the current booking being rescheduled
        if (b.id === selectedBookingId) return false;

        // Skip cancelled bookings
        if (b.status === "cancelled") return false;

        // Only check bookings for the same event type on the same date
        if (b.eventTypeId !== booking.eventTypeId) return false;
        if (b.date !== formattedDate) return false;

        // Parse existing booking times
        const existingStart = dayjs(b.timeStart, [
          "h:mma",
          "h:mm a",
          "HH:mm",
          "H:mm",
        ]);
        const existingEnd = dayjs(b.timeEnd, [
          "h:mma",
          "h:mm a",
          "HH:mm",
          "H:mm",
        ]);

        // Check for time overlap
        // Two time ranges overlap if: start1 < end2 AND start2 < end1
        const hasOverlap =
          newStartTime.isBefore(existingEnd) &&
          existingStart.isBefore(newEndTime);

        return hasOverlap;
      });

      if (conflictingBooking) {
        setAlert({
          open: true,
          message: `This time slot is already booked by ${conflictingBooking.name}. Please select a different time.`,
          severity: "error",
        });
        setSendingEmail(false);
        return;
      }

      // Validate selected time is within availability hours
      if (rescheduleAvailability) {
        const dateString = rescheduleDate.format("YYYY-MM-DD");

        // Check for override on this specific date
        const override = rescheduleAvailability.overrides?.find(
          (o) => o.date === dateString
        );

        let timeSlots = rescheduleAvailability.timeSlots || [];
        if (override && override.timeSlots && override.timeSlots.length > 0) {
          timeSlots = override.timeSlots;
        }

        // Check if selected time falls within any available time slot
        const selectedHour = rescheduleTime.hour();
        const selectedMinute = rescheduleTime.minute();
        const selectedTimeInMinutes = selectedHour * 60 + selectedMinute;

        const isWithinAvailability = timeSlots.some((slot) => {
          // Parse time slot start and end (handles formats like "09:00 am", "9:00am", "09:00")
          const parseTime = (timeStr) => {
            const normalized = timeStr.toLowerCase().replace(/\s+/g, "");
            let hours, minutes;
            const match = normalized.match(/(\d{1,2}):?(\d{2})?(am|pm)?/);
            if (match) {
              hours = parseInt(match[1], 10);
              minutes = parseInt(match[2] || "0", 10);
              const period = match[3];
              if (period === "pm" && hours !== 12) hours += 12;
              if (period === "am" && hours === 12) hours = 0;
            }
            return hours * 60 + minutes;
          };

          const slotStart = parseTime(slot.timeStart);
          const slotEnd = parseTime(slot.timeEnd);

          return (
            selectedTimeInMinutes >= slotStart &&
            selectedTimeInMinutes < slotEnd
          );
        });

        if (!isWithinAvailability && timeSlots.length > 0) {
          const availableTimesStr = timeSlots
            .map((slot) => `${slot.timeStart} - ${slot.timeEnd}`)
            .join(", ");
          setAlert({
            open: true,
            message: `Selected time is outside available hours. Available times: ${availableTimesStr}`,
            severity: "error",
          });
          setSendingEmail(false);
          return;
        }
      }

      // Store old booking details for email
      const oldBookingData = {
        date: booking.date,
        timeStart: booking.timeStart,
        timeEnd: booking.timeEnd,
        timezone: booking.timezone || "UTC",
      };

      // Update booking in database - preserve the status
      const response = await axiosInstance.post("/graphql", {
        query: UPDATE_BOOKING,
        variables: {
          id: selectedBookingId,
          input: {
            eventTypeId: booking.eventTypeId,
            date: formattedDate,
            timeStart: formattedTimeStart,
            timeEnd: formattedTimeEnd,
            name: booking.name,
            email: booking.email,
            notes: booking.notes,
            timezone: rescheduleTimezone || booking.timezone,
            status: booking.status || "confirmed", // Preserve existing status
          },
        },
      });

      if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || "Unknown error");
      }

      // Send reschedule notification email - continue even if this fails
      try {
        await sendEmail(booking, eventType, "reschedule", {
          oldDate: booking.date,
          oldTime: `${booking.timeStart} - ${booking.timeEnd}`,
          oldTimezone: booking.timezone || "UTC",
          newDate: formattedDate,
          newTime: `${formattedTimeStart} - ${formattedTimeEnd}`,
          newTimezone: rescheduleTimezone || booking.timezone,
        });
        console.log("Reschedule email sent successfully");
      } catch (emailError) {
        console.warn(
          "Email sending had an issue but we'll continue:",
          emailError
        );
        // Continue with the process even if email fails
      }

      // Update local state
      const updatedBookings = bookings.map((b) => {
        if (b.id === selectedBookingId) {
          return {
            ...b,
            date: formattedDate,
            timeStart: formattedTimeStart,
            timeEnd: formattedTimeEnd,
            timezone: rescheduleTimezone || b.timezone,
          };
        }
        return b;
      });

      setBookings(updatedBookings);
      setSelected([]);

      setAlert({
        open: true,
        message: "Booking rescheduled successfully and email sent to attendee",
        severity: "success",
      });
    } catch (error) {
      console.error("Error rescheduling booking:", error);
      setAlert({
        open: true,
        message: `Error rescheduling booking: ${error.message}`,
        severity: "error",
      });
    } finally {
      setSendingEmail(false);
      setRescheduleDialog(false);
    }
  };

  // Confirm request reschedule
  const confirmRequestReschedule = async () => {
    if (!selectedBookingId) return;

    try {
      setSendingEmail(true);

      // Find the booking
      const booking = bookings.find((b) => b.id === selectedBookingId);
      if (!booking) throw new Error("Booking not found");

      // Fetch event type data
      const eventType = await fetchEventTypeData(booking.eventTypeId);
      if (!eventType) throw new Error("Event type not found");

      // Send request reschedule email - continue even if this fails
      try {
        await sendEmail(booking, eventType, "requestReschedule", {
          bookingPreviewUrl: `${window.location.origin}/preview/preview-event-type?eventTypeId=${eventType.id}`,
        });
        console.log("Request reschedule email sent successfully");
      } catch (emailError) {
        console.warn(
          "Email sending had an issue but we'll continue:",
          emailError
        );
        // Continue with the process even if email fails
      }

      setAlert({
        open: true,
        message: "Reschedule request sent to attendee",
        severity: "success",
      });
      setSelected([]);
    } catch (error) {
      console.error("Error requesting reschedule:", error);
      setAlert({
        open: true,
        message: `Error requesting reschedule: ${error.message}`,
        severity: "error",
      });
    } finally {
      setSendingEmail(false);
      setRequestRescheduleDialog(false);
    }
  };

  // Handle delete booking (supports bulk delete)
  const handleDeleteBooking = () => {
    if (selected.length === 0) return;
    setSelectedBookingIds([...selected]); // Store ALL selected IDs for bulk delete
    setSelectedBookingId(selected[0]); // Keep for backward compatibility
    setDeleteDialog(true);
  };

  // Confirm delete booking (supports bulk delete)
  const confirmDeleteBooking = async () => {
    // Use bulk IDs if available, otherwise fall back to single ID
    const idsToDelete =
      selectedBookingIds.length > 0
        ? selectedBookingIds
        : selectedBookingId
        ? [selectedBookingId]
        : [];

    if (idsToDelete.length === 0) return;

    try {
      setLoading(true);

      // Use bulk delete mutation
      const response = await axiosInstance.post("/graphql", {
        query: DELETE_BOOKINGS,
        variables: {
          ids: idsToDelete,
        },
      });

      console.log("Delete bookings response:", response.data);

      if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || "Unknown error");
      }

      // Check if deletion was successful
      const result =
        response.data?.data?.deleteBookings || response.data?.deleteBookings;

      if (!result) {
        throw new Error("Failed to delete bookings");
      }

      console.log("Bookings deleted successfully:", result);

      // Update local state - remove all deleted bookings
      const deletedIdsSet = new Set(result.deletedIds);
      const updatedBookings = bookings.filter((b) => !deletedIdsSet.has(b.id));
      setBookings(updatedBookings);
      setSelected([]);

      // Show appropriate success message
      const count = result.deletedCount;
      let message =
        count === 1
          ? "Booking deleted successfully"
          : `${count} bookings deleted successfully`;

      // Add error info if some deletions failed
      if (result.errors && result.errors.length > 0) {
        message += ` (${result.errors.length} failed)`;
      }

      setAlert({
        open: true,
        message,
        severity: result.success ? "success" : "warning",
      });
    } catch (error) {
      console.error("Error deleting bookings:", error);
      setAlert({
        open: true,
        message: `Error deleting bookings: ${error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
      setDeleteDialog(false);
      setSelectedBookingId(null);
      setSelectedBookingIds([]);
    }
  };

  // Handle Accept/Reject booking
  const handleAcceptReject = async (id, action) => {
    // Set loading state for the specific button
    setLoadingActions((prev) => ({
      ...prev,
      [id]: action,
    }));

    try {
      // Find the booking
      const booking = bookings.find((b) => b.id === id);
      if (!booking) throw new Error("Booking not found");

      // Fetch event type data
      const eventType = await fetchEventTypeData(booking.eventTypeId);
      if (!eventType) throw new Error("Event type not found");

      // Determine new status
      const newStatus = action === "accept" ? "confirmed" : "rejected";

      // Update booking status in database
      const response = await axiosInstance.post("/graphql", {
        query: UPDATE_BOOKING,
        variables: {
          id,
          input: {
            eventTypeId: booking.eventTypeId,
            date: booking.date,
            timeStart: booking.timeStart,
            timeEnd: booking.timeEnd,
            name: booking.name,
            email: booking.email,
            notes: booking.notes,
            timezone: booking.timezone,
            status: newStatus,
          },
        },
      });

      if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || "Unknown error");
      }

      // Send confirmation or rejection email - continue even if this fails
      try {
        if (action === "accept") {
          await sendEmail(booking, eventType, "bookingConfirmation");
        } else {
          await sendEmail(booking, eventType, "bookingRejection", {
            bookingPreviewUrl: `${window.location.origin}/preview/preview-event-type?eventTypeId=${eventType.id}`,
          });
        }
        console.log(
          `${
            action === "accept" ? "Confirmation" : "Rejection"
          } email sent successfully`
        );
      } catch (emailError) {
        console.warn(
          "Email sending had an issue but we'll continue:",
          emailError
        );
        // Continue with the process even if email fails
      }

      // Update local state
      const updatedBookings = bookings.map((b) => {
        if (b.id === id) {
          return {
            ...b,
            status: newStatus,
          };
        }
        return b;
      });

      setBookings(updatedBookings);

      setAlert({
        open: true,
        message: `Booking ${
          action === "accept" ? "accepted" : "rejected"
        } successfully`,
        severity: "success",
      });
    } catch (error) {
      console.error(
        `Error ${action === "accept" ? "accepting" : "rejecting"} booking:`,
        error
      );
      setAlert({
        open: true,
        message: `Error ${
          action === "accept" ? "accepting" : "rejecting"
        } booking: ${error.message}`,
        severity: "error",
      });
    } finally {
      // Reset loading state
      setLoadingActions((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    }
  };

  // Fetch bookings for the current space
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post("/graphql", {
        query: GET_BOOKINGS_BY_SPACE,
        variables: { spaceId: currentSpaceId },
      });

      if (response?.data?.getBookingsBySpace) {
        const bookingsData = response.data.getBookingsBySpace;
        setBookings(bookingsData);

        // Fetch event type data for each booking
        bookingsData.forEach((booking) => {
          fetchEventTypeData(booking.eventTypeId);
        });
      } else {
        setBookings([]);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setAlert({
        open: true,
        message: "Error fetching bookings",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentSpaceId) {
      fetchBookings();
    }
  }, [currentSpaceId]);

  // Handle kill meeting link
  const handleKillMeetingLink = async () => {
    if (!selectedBookingId) return;

    try {
      setKillingLink(true);

      const response = await axiosInstance.post("/graphql", {
        query: KILL_MEETING_LINK,
        variables: { bookingId: selectedBookingId },
      });

      if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || "Unknown error");
      }

      // Update local state
      const result =
        response.data?.data?.killMeetingLink || response.data?.killMeetingLink;

      if (result) {
        const updatedBookings = bookings.map((b) => {
          if (b.id === selectedBookingId) {
            return { ...b, meetingInfo: result.meetingInfo };
          }
          return b;
        });
        setBookings(updatedBookings);
      } else {
        // Fallback: refetch all bookings
        await fetchBookings();
      }

      setAlert({
        open: true,
        message: "Meeting link killed successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error killing meeting link:", error);
      setAlert({
        open: true,
        message: `Error killing meeting link: ${error.message}`,
        severity: "error",
      });
    } finally {
      setKillingLink(false);
      setKillLinkDialog(false);
      setSelectedBookingId(null);
    }
  };

  // Reset page when filter changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filterStatus]);

  // Update filter when URL status param changes
  useEffect(() => {
    if (urlStatusFilter) {
      const newFilter = getInitialFilter();
      if (newFilter !== filterStatus) {
        setFilterStatus(newFilter);
      }
    }
  }, [urlStatusFilter]);

  return (
    <React.Fragment>
      <Helmet title="Bookings" />

      <Grid justifyContent="space-between" container spacing={6}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Bookings
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to={`/spaces/${currentSpaceId}`}>
              {currentSpace?.name || "Spaces"}
            </Link>
            <Link
              component={NavLink}
              to={`/calendar/dashboard?spaceId=${currentSpaceId}`}
            >
              Calendar
            </Link>
            <Typography color="text.primary">Bookings</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />

      {/* Search bar - using consistent SearchBar component */}
      <SearchBar
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder="Search bookings..."
        containerSx={{
          borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
          mb: 2,
        }}
      />

      {/* Filters section */}
      <BookingFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
      />

      {/* Table section */}
      <Paper>
        <BookingsTable
          loading={loading}
          bookings={filteredBookings}
          eventTypesData={eventTypesData}
          selected={selected}
          setSelected={setSelected}
          loadingActions={loadingActions}
          viewStyle={viewStyle}
          setViewStyle={setViewStyle}
          handleCancelBooking={handleCancelBooking}
          handleRescheduleBooking={handleRescheduleBooking}
          handleRequestReschedule={handleRequestReschedule}
          handleDeleteBooking={handleDeleteBooking}
          handleAcceptReject={handleAcceptReject}
          onMeetingSummaryClick={handleMeetingSummaryClick}
          onEndMeeting={handleEndMeeting}
          setSelectedBookingId={setSelectedBookingId}
          setCancelDialog={setCancelDialog}
          setRescheduleDialog={setRescheduleDialog}
          setRequestRescheduleDialog={setRequestRescheduleDialog}
          setDeleteDialog={setDeleteDialog}
          setRescheduleDate={setRescheduleDate}
          setRescheduleTime={setRescheduleTime}
          setRescheduleTimezone={setRescheduleTimezone}
          setKillLinkDialog={setKillLinkDialog}
          // Pass pagination props
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
        />
      </Paper>

      {/* Dialogs */}
      <BookingDialogs
        killLinkDialog={killLinkDialog}
        setKillLinkDialog={setKillLinkDialog}
        confirmKillLink={handleKillMeetingLink}
        killingLink={killingLink}
        cancelDialog={cancelDialog}
        setCancelDialog={setCancelDialog}
        rescheduleDialog={rescheduleDialog}
        setRescheduleDialog={setRescheduleDialog}
        requestRescheduleDialog={requestRescheduleDialog}
        setRequestRescheduleDialog={setRequestRescheduleDialog}
        deleteDialog={deleteDialog}
        setDeleteDialog={setDeleteDialog}
        selectedBookingIds={selectedBookingIds}
        rescheduleDate={rescheduleDate}
        setRescheduleDate={setRescheduleDate}
        rescheduleTime={rescheduleTime}
        setRescheduleTime={setRescheduleTime}
        rescheduleTimezone={rescheduleTimezone}
        setRescheduleTimezone={setRescheduleTimezone}
        sendingEmail={sendingEmail}
        confirmCancelBooking={confirmCancelBooking}
        confirmRescheduleBooking={confirmRescheduleBooking}
        confirmRequestReschedule={confirmRequestReschedule}
        confirmDeleteBooking={confirmDeleteBooking}
        availabilityData={rescheduleAvailability}
        loadingAvailability={loadingAvailability}
        eventTypeData={rescheduleEventType}
        existingBookings={bookings}
      />

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default Bookings;
